/**
 * Web Search Tool Configurations
 *
 * Tools for searching the web using Linkup and Exa AI.
 */ import { defineToolConfig, formatCount, hasProperty } from '../tool-display-config';
// ============================================================================
// Helper Functions
// ============================================================================
function isWebSearchSuccess(output) {
    return typeof output === 'object' && output !== null && 'success' in output && output.success === true && 'data' in output;
}
function isWebSearchError(output) {
    return typeof output === 'object' && output !== null && 'success' in output && output.success === false && 'error' in output;
}
// ============================================================================
// Tool Configurations
// ============================================================================
const linkupSearchConfig = defineToolConfig({
    id: 'linkup-search',
    aliases: [
        'linkupSearch',
        'linkupSearchTool',
        'webSearchTool'
    ],
    formatSuccess: (output)=>{
        if (isWebSearchError(output)) {
            return `Search failed: ${output.error.message}`;
        }
        if (isWebSearchSuccess(output)) {
            const sourceCount = output.data.sources?.length ?? 0;
            if (sourceCount > 0) {
                return `Found answer with ${formatCount(sourceCount, 'source')}: "${output.data.answer}"`;
            }
            return `"${output.data.answer}"`;
        }
        return 'Web search completed';
    },
    formatError: (error)=>{
        if (isWebSearchError(error)) {
            return `Search failed: ${error.error.message}`;
        }
        if (hasProperty(error, 'message')) {
            return `Search failed: ${error.message}`;
        }
        return 'Web search failed';
    },
    formatInput: (input)=>{
        if (!input?.query) return [];
        const fields = [
            {
                label: 'Query',
                value: input.query
            }
        ];
        if (input.depth && input.depth !== 'standard') {
            fields.push({
                label: 'Depth',
                value: input.depth
            });
        }
        if (input.includeDomains?.length) {
            fields.push({
                label: 'Domains',
                value: input.includeDomains.join(', ')
            });
        }
        return fields;
    },
    isValidOutput: (output)=>{
        return isWebSearchSuccess(output) || isWebSearchError(output);
    },
    isErrorResult: (output)=>isWebSearchError(output)
});
const exaSearchConfig = defineToolConfig({
    id: 'exa-search',
    aliases: [
        'exaSearch',
        'exaSearchTool'
    ],
    formatSuccess: (output)=>{
        if (isWebSearchError(output)) {
            return `Search failed: ${output.error.message}`;
        }
        if (isWebSearchSuccess(output)) {
            const citationCount = output.data.citations?.length ?? 0;
            if (citationCount > 0) {
                return `Found answer with ${formatCount(citationCount, 'citation')}: "${output.data.answer}"`;
            }
            return `"${output.data.answer}"`;
        }
        return 'Exa search completed';
    },
    formatError: (error)=>{
        if (isWebSearchError(error)) {
            return `Exa search failed: ${error.error.message}`;
        }
        if (hasProperty(error, 'message')) {
            return `Exa search failed: ${error.message}`;
        }
        return 'Exa search failed';
    },
    formatInput: (input)=>{
        if (!input?.query) return [];
        return [
            {
                label: 'Query',
                value: input.query
            }
        ];
    },
    isValidOutput: (output)=>{
        return isWebSearchSuccess(output) || isWebSearchError(output);
    },
    isErrorResult: (output)=>isWebSearchError(output)
});
// ============================================================================
// Export
// ============================================================================
export const webSearchToolConfigs = [
    linkupSearchConfig,
    exaSearchConfig
];
